import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import "./CampaignDetailPage.css";
import "../components/MessagingButtons.css";
import "../components/MessagingPage.css";
import "../components/ClientSelectableTable.css";
import { useAuth } from "../../../hooks/useAuth";
import { getCampaignByIdAsync } from "../../../api/MessageApi";
import { useMessageLogs } from "../../../hooks/useMessageLogs";
import { useClients } from "../../../hooks/useClients";
import { Constants } from "../../../constants/constants";
import {
  MessageCampaign,
  MessageChannel,
  MessageType,
  MESSAGE_CHANNEL_LABELS,
  MESSAGE_TYPE_LABELS,
} from "../../../models/MessageModels";
import CampaignStatusBadge from "../components/CampaignStatusBadge";
import MessageStatusBadge from "../components/MessageStatusBadge";
import { formatDateTime } from "../../../utils/DateTime";
import Toast from "../../../components/Toast";

const truncate = (text: string, max: number): string =>
  text.length > max ? `${text.slice(0, max)}…` : text;

const CampaignDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const organizationId = user?.activeOrganization?.organizationId;

  const {
    logs,
    loading: logsLoading,
    error: logsError,
    reload: reloadLogs,
  } = useMessageLogs(organizationId);
  const { clients, loading: clientsLoading } = useClients(organizationId, {
    pageSize: 1000,
  });

  const [campaign, setCampaign] = useState<MessageCampaign | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error" | "info";
  } | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    const token = localStorage.getItem(Constants.ACCESS_TOKEN) || "";
    getCampaignByIdAsync(token, id)
      .then((res) => {
        if (cancelled) return;
        if (res.success && res.result) {
          setCampaign(res.result);
          setError(null);
        } else {
          setError(res.message || "Campaign not found");
        }
      })
      .catch((err) => {
        console.error(err);
        if (!cancelled) setError("Failed to load campaign");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const clientNameMap = useMemo(
    () => new Map(clients.map((client) => [client.id, client.fullName])),
    [clients],
  );

  const recipientLogs = useMemo(
    () => logs.filter((log) => log.campaignId === id),
    [logs, id],
  );

  return (
    <div className="msg-page">
      <div className="msg-page__header">
        <div>
          <h2>{campaign?.name || "Campaign"}</h2>
          <p>
            {campaign
              ? `${MESSAGE_TYPE_LABELS[campaign.messageType as MessageType] ?? "Message"} · ${
                  MESSAGE_CHANNEL_LABELS[campaign.channel as MessageChannel] ?? "—"
                } · created ${formatDateTime(campaign.createdAt)}`
              : "Campaign details"}
          </p>
        </div>
      </div>

      {loading || !campaign ? (
        <div className="msg-table-empty">
          {loading ? "Loading campaign…" : error || "Campaign not found"}
          {error && !loading && (
            <button
              className="msg-btn msg-btn--secondary"
              onClick={() => window.location.reload()}
            >
              Retry
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="msg-detail__summary">
            <div className="msg-detail__card">
              <span className="msg-detail__value">{campaign.totalCount}</span>
              <span className="msg-detail__label">Total recipients</span>
            </div>
            <div className="msg-detail__card msg-detail__card--success">
              <span className="msg-detail__value">{campaign.successCount}</span>
              <span className="msg-detail__label">Sent</span>
            </div>
            <div className="msg-detail__card msg-detail__card--failed">
              <span className="msg-detail__value">{campaign.failCount}</span>
              <span className="msg-detail__label">Failed</span>
            </div>
            <div className="msg-detail__card">
              <span className="msg-detail__status">
                <CampaignStatusBadge status={campaign.status} />
              </span>
              <span className="msg-detail__label">Status</span>
            </div>
          </div>

          <div className="msg-detail__recipients">
            <h3>Recipients ({recipientLogs.length})</h3>
            <p className="msg-detail__note">
              Loaded from the org-wide message log and filtered by this
              campaign. <strong>Sent</strong> = message prepared / WhatsApp
              opened; true delivery reports need provider integration later.
            </p>

            {logsError ? (
              <div className="msg-table-empty">
                <p>{logsError}</p>
                <button className="msg-btn msg-btn--secondary" onClick={reloadLogs}>
                  Retry
                </button>
              </div>
            ) : logsLoading || clientsLoading ? (
              <div className="msg-table-empty">Loading recipients…</div>
            ) : recipientLogs.length === 0 ? (
              <div className="msg-table-empty">
                No recipient logs found for this campaign.
              </div>
            ) : (
              <div className="msg-table__wrap">
                <table className="msg-table__grid msg-detail__table">
                  <thead>
                    <tr>
                      <th>Client</th>
                      <th>Phone</th>
                      <th>Status</th>
                      <th>Sent at</th>
                      <th>Error</th>
                      <th>Body</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recipientLogs.map((log) => (
                      <tr key={log.id}>
                        <td className="msg-detail__name">
                          {clientNameMap.get(log.clientId) ?? "Unknown client"}
                        </td>
                        <td>{log.phone || "—"}</td>
                        <td>
                          <MessageStatusBadge status={log.status} />
                        </td>
                        <td className="msg-detail__muted">
                          {log.sentAt ? formatDateTime(log.sentAt) : "—"}
                        </td>
                        <td className="msg-detail__muted">
                          {log.errorMessage || "—"}
                        </td>
                        <td className="msg-detail__body">
                          <span title={log.body}>{truncate(log.body, 120)}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default CampaignDetailPage;